import React from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAccountState } from "../hooks/useAccountState";
import type { FeatureKey } from "../lib/api";
import { canUseFeature, FEATURE_LOCK_MESSAGE } from "../lib/featureAccess";

type RequestCardProps = {
  title: string;
  description: string;
  status: "ready" | "coming_soon";
  feature?: FeatureKey;
  locked?: boolean;
  onClick?: () => void;
  onLockedClick?: () => void;
};

function RequestCard({ title, description, status, locked = false, onClick, onLockedClick }: RequestCardProps) {
  const isReady = status === "ready";
  const isLocked = isReady && locked;
  return (
    <button
      type="button"
      onClick={isLocked ? onLockedClick : onClick}
      disabled={!isReady && !isLocked}
      aria-disabled={isLocked ? "true" : undefined}
      className={`w-full rounded-[1.8rem] border p-5 text-left shadow-[0_12px_28px_rgba(124,90,78,0.10)] transition ${
        isLocked
          ? "border-[#E0D5CE] bg-[#F4EEE9] opacity-85"
          : isReady
          ? "border-[#EADBC8] bg-white hover:-translate-y-0.5 hover:bg-[#FFFCFA]"
          : "cursor-not-allowed border-[#EEE3DB] bg-[#FAF6F2] opacity-75"
      }`}
    >
      <span
        className={`inline-flex rounded-full px-3 py-1 text-[11px] font-black ${
          isLocked
            ? "bg-white text-[#A1887D]"
            : isReady
            ? "bg-[#FFF3E9] text-[#A46D4C]"
            : "bg-white text-[#A1887D]"
        }`}
      >
        {isLocked ? "ロック中" : isReady ? "すぐ使える" : "準備中"}
      </span>
      <h2 className="mt-4 text-lg font-black text-[#5D4D46]">{title}</h2>
      <p className="mt-2 text-sm leading-6 text-[#6B5851]">{description}</p>
      <p className="mt-4 text-sm font-bold text-[#A67C52]">
        {isLocked ? FEATURE_LOCK_MESSAGE : isReady ? "開く →" : "今後追加予定"}
      </p>
    </button>
  );
}

export default function RequestEntry() {
  const navigate = useNavigate();
  const { accountState, loading: accountStateLoading } = useAccountState();
  const [lockedMessage, setLockedMessage] = React.useState<string | null>(null);

  const isLocked = (feature: FeatureKey) =>
    accountStateLoading || !accountState || !canUseFeature(accountState, feature);
  const showLockedMessage = () => setLockedMessage(FEATURE_LOCK_MESSAGE);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-md">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/home" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ← HOME
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">メイに頼む</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2.4rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#A67C52]">REQUEST HUB</p>
            <h1 className="mt-3 text-3xl font-black tracking-tight text-[#7C5A4E]">メイに頼む</h1>
            <p className="mt-3 text-sm leading-7 text-[#6B5851]">
              調べもの・整理・通知を、AIエージェントに任せられます。
            </p>
          </div>

          <div className="mt-5 rounded-[1.4rem] border border-[#EADBC8] bg-[#FFF8F4] p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-black text-[#5D4D46]">ニュース・レポートの定期配信</h2>
                <p className="mt-1 text-sm leading-6 text-[#6B5851]">設定済みの配信を停止・再開・削除できます。</p>
              </div>
              <Link
                to="/request/schedules"
                className="inline-flex min-h-[40px] shrink-0 items-center justify-center rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#5D4D46]"
              >
                定期配信を管理
              </Link>
            </div>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {lockedMessage ? (
              <p role="status" className="sm:col-span-2 rounded-xl bg-[#FFF8F1] px-4 py-3 text-sm font-bold text-[#A67C52]">
                {lockedMessage}
              </p>
            ) : null}
            <RequestCard
              title="ニュース"
              description="気になるテーマのニュースを短く整理して届けます。"
              status="ready"
              feature="news"
              locked={isLocked("news")}
              onLockedClick={showLockedMessage}
              onClick={() => navigate("/request/news")}
            />
            <RequestCard
              title="企業リサーチ"
              description="企業名やURLから公開情報を整理し、商談前の確認に使えます。"
              status="ready"
              feature="company_research"
              locked={isLocked("company_research")}
              onLockedClick={showLockedMessage}
              onClick={() => navigate("/request/company-research", { state: { startNew: true } })}
            />
            <RequestCard
              title="レポート"
              description="テーマを深く整理し、考える材料をまとめます。"
              status="ready"
              feature="report"
              locked={isLocked("report")}
              onLockedClick={showLockedMessage}
              onClick={() => navigate("/request/report")}
            />
            <RequestCard
              title="領収書整理"
              description="写真を送るだけで、あとから見返しやすい形に整理します。"
              status="ready"
              feature="receipt_ocr"
              locked={isLocked("receipt_ocr")}
              onLockedClick={showLockedMessage}
              onClick={() => navigate("/receipt", { state: { returnTo: "/request" } })}
            />
            <RequestCard
              title="社長メモ"
              description="写真を撮るだけで、文字起こし・要点整理をして保存します。"
              status="ready"
              feature="memo_ocr"
              locked={isLocked("memo_ocr")}
              onLockedClick={showLockedMessage}
              onClick={() => navigate("/request/executive-memos")}
            />
            <RequestCard
              title="名刺整理"
              description="名刺情報を取り込み、あとから探しやすくします。"
              status="coming_soon"
            />
            <RequestCard
              title="スケジュール調整"
              description="予定確認や日程調整を手伝います。"
              status="coming_soon"
            />
            <RequestCard
              title="移動・出張手配"
              description="移動ルートや候補整理をサポートします。"
              status="coming_soon"
            />
            <RequestCard title="代理応答" description="返信文のたたき台や一次対応を支援します。" status="coming_soon" />
            <RequestCard
              title="物品購入サポート"
              description="必要な備品や消耗品の候補探し・比較を支援します。"
              status="coming_soon"
            />
          </div>
        </section>
      </div>
    </main>
  );
}
